"use client";

import * as React from "react";
import { ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { prefersReducedMotion } from "@/lib/use-prefers-reduced-motion";

const SCROLL_THRESHOLD = 300;

export function ScrollToTopButton() {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > SCROLL_THRESHOLD);
    };

    // Check initial position
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    const html = document.documentElement;
    const mainContent = document.getElementById("main-content");

    if (prefersReducedMotion()) {
      // Instant scroll - no animation
      html.scrollTop = 0;
      mainContent?.focus({ preventScroll: true });
    } else {
      // Temporarily disable CSS smooth scroll to take full control
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";

      // Use custom smooth scroll animation for more reliable behavior
      const startY = window.scrollY;
      const startTime = performance.now();
      const duration = 500; // ms

      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);

        html.scrollTop = startY * (1 - eased);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          // Ensure we're at exactly 0
          html.scrollTop = 0;
          // Restore scroll behavior
          html.style.scrollBehavior = prevBehavior;
          // Focus for accessibility
          mainContent?.focus({ preventScroll: true });
        }
      };

      requestAnimationFrame(animateScroll);
    }
  };

  // Wrap in a fixed container so the tooltip's inline-flex wrapper
  // doesn't affect document flow (which would add space below the footer)
  return (
    <div
      className={cn(
        "fixed right-6 bottom-6 z-50",
        "transition-all duration-300 motion-reduce:transition-none",
        isVisible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none invisible translate-y-4 opacity-0",
      )}
    >
      <Button
        variant="outline"
        size="icon"
        aria-label="Scroll to top"
        data-testid="scroll-to-top-button"
        data-tooltip="Scroll to top"
        data-tooltip-side="left"
        onClick={scrollToTop}
        className={cn(
          "rounded-full shadow-lg",
          "border-border bg-background/80 hover:bg-muted backdrop-blur-lg",
          "hover:shadow-xl",
        )}
      >
        <ArrowUp className="size-5" aria-hidden="true" />
      </Button>
    </div>
  );
}
